import React, { Component } from 'react';
import { Helmet } from 'react-helmet-async';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { logError } from 'commons/logger';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';

import Breadcrumb from 'components/consumer/consumer-breadcrumb';
import { algoliaClient } from 'components/essentials/AlgoliaSearchClient';
import AllOptions from 'data-hooks/useAllOptions/AllOptions';
import scrollTo from 'commons/scrollTo';
import pollAsync from 'commons/pollAsync';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import ProductsGridStatic from '../products-grid/ProductsGridStatic';
import MaterialSwatch from './material-swatch';
import getMaterialNameFromSlug from './getMaterialNameFromSlug';

const ProductsGridStaticWithData = ({ productIds, material, listName }) => {
  const { loading, products } = useStaticProductCardData({
    productIds,
    upholsteryLabelFilters: [material],
  });
  return (
    <ProductsGridStatic
      columnsOnDesktop="4"
      columnsOnMobile="2"
      layout="minimal"
      listName={listName}
      loading={loading}
      maxCardsCount={2}
      productsData={products}
    />
  );
};

class ProductsByMaterial extends Component {
  constructor(props) {
    super(props);
    const queryParams = new URLSearchParams(this.props.location.search);

    this.state = {
      page: 0,
      hitsPerPage: 15,
      material: getMaterialNameFromSlug(this.props.match.params.swatch),
      materialType: 'Fabric',
      searchLoading: true,
      fabricValueId: null,
      productIds: [],
      errorState: false,
      isInactiveView: !!queryParams.get('showInactive'),
      searchFinished: false,
      inactiveOptionSearchStep: 0,
      isSearching: false,
      fabricImage: '',
      fabricDescription: '',
      fabricValue: '',
    };
  }

  componentDidMount() {
    scrollTo(0, 0);
    this.setState(
      {
        searchIndex: algoliaClient.initIndex(ALGOLIA_INDEX_NAME.PRODUCTS),
      },
      async () => {
        await pollAsync({
          condition: () => !AllOptions.getAllOptions()[0],
          maxRetries: 100,
        });
        this.searchProductIndex();
      }
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !prevState.productIds?.length &&
      !this.state.productIds?.length &&
      this.state.searchFinished &&
      this.state.isInactiveView &&
      this.state.inactiveOptionSearchStep === 0 &&
      !this.state.isSearching
    ) {
      this.setState(
        {
          optionSearchIndex: algoliaClient.initIndex('product_option'),
        },
        () => {
          this.searchProductOptionIndex();
        }
      );
    }
    // First load of inactive products
    if (
      prevState.inactiveOptionSearchStep === 0 &&
      +this.state.inactiveOptionSearchStep === 1 &&
      !this.state.isSearching &&
      this.state.isInactiveView
    ) {
      this.loadMoreInactiveOptionProducts();
    }
  }

  searchProductIndex = () => {
    const {
      hitsPerPage,
      material,
      page: statePage,
      searchIndex,
      productIds,
    } = this.state;

    searchIndex
      .search('', {
        hitsPerPage,
        filters: `option_value:"${material}"`,
        page: statePage,
        attributesToRetrieve: [
          'product_id',
          'option_value',
          'option_name',
          'default_material_type',
        ],
        numericFilters: ['item_availability=0', 'visibility>0', 'visibility<4'],
      })
      .then(async response => {
        const { hits = [] } = response;
        const productIDS = hits.map(product =>
          parseInt(product.product_id, 10)
        );
        let updatedProductIds = productIds;
        updatedProductIds = updatedProductIds.concat(productIDS);
        const [, allOptionsData] = AllOptions.getAllOptions();
        let fabricValueId = null;
        if (productIDS.length && allOptionsData) {
          for (const optKey of Object.entries(allOptionsData)) {
            if (
              optKey[1].value &&
              material &&
              optKey[1].value.toLowerCase().trim() ===
                material.toLowerCase().trim()
            ) {
              [fabricValueId] = optKey;
              break;
            }
          }
        }

        this.setState({
          searchLoading: false,
          productIds: updatedProductIds,
          fabricValueId,
          materialType: productIDS.length
            ? hits[0].default_material_type
            : material,
          searchFinished: true,
        });
      })
      .catch(e => logError(e));
  };

  searchProductOptionIndex = async () => {
    if (!this.state.optionSearchIndex) {
      return;
    }
    this.setState(() => ({
      isSearching: true,
    }));
    this.state.optionSearchIndex
      .search('', {
        filters: `option_value:"${this.state.material}"`,
        hitsPerPage: 1,
        page: 0,
        attributesToRetrieve: [
          'option_description',
          'option_value',
          'option_value_id',
          'option_name',
          'product_id',
          'option_image',
        ],
      })
      .then(async optProducts => {
        const { hits = [] } = optProducts;
        const optData = hits?.[0] || {};
        this.setState({
          searchLoading: false,
          isSearching: false,
          productIds: [],
          fabricValueId: optData?.option_value_id?.toString() || null,
          fabricValue: optData?.option_value,
          fabricImage: optData?.option_image,
          fabricDescription: optData?.option_description,
          materialType: optData?.option_name,
          inactiveOptionSearchStep: 1,
        });
      });
  };

  loadMoreInactiveOptionProducts = () => {
    this.state.searchIndex
      .search({
        query: '',
        hitsPerPage: this.state.hitsPerPage,
        filters: `option_name:"${this.state.materialType}"`,
        page: this.state.page,
        attributesToRetrieve: ['product_id'],
        numericFilters: ['item_availability=0', 'visibility>0', 'visibility<4'],
      })
      .then(async products => {
        const { hits = [], page, nbPages } = products;
        const productIDS = hits.map(product =>
          parseInt(product.product_id, 10)
        );
        this.setState(prevState => {
          let updatedProductIds = prevState.productIds;
          updatedProductIds = updatedProductIds.concat(productIDS);
          return {
            searchLoading: false,
            productIds: updatedProductIds,
            hasMore: page < nbPages,
            searchFinished: true,
            inactiveOptionSearchStep: 2,
          };
        });
      });
  };

  render() {
    if (
      this.state.searchLoading ||
      (this.state.isInactiveView && this.state.isSearching)
    )
      return (
        <div className="flex flex-col w-full min-h-[50vh] items-center justify-center text-[.9rem] text-gray">
          Loading
        </div>
      );
    if (this.state.errorState)
      return (
        <div className="flex flex-col w-full min-h-[50vh] items-center justify-center text-[.9rem] text-gray">
          Could not retrieve products for this swatch. Please try again.
        </div>
      );

    return (
      <>
        <Breadcrumb
          config={{
            items: [
              { label: 'Home', link: '/' },
              { label: 'Material Swatches', link: '/swatches' },
              { label: this.state.material },
            ],
          }}
        />
        {this.state.fabricValueId ? (
          <div className="flex flex-col max-w-[1143px] mx-auto my-0 pt-8">
            {!!this.state.material && (
              <Helmet>
                <title>Swatches | {this.state.material}</title>
              </Helmet>
            )}
            {this.state.productIds.length > 0 && (
              <div className="[border-bottom:1px_solid_#d7d7d7] mb-6">
                <MaterialSwatch
                  fabricValueId={this.state.fabricValueId}
                  staticContext={this.props.staticContext}
                  fabricValueName={this.state.fabricValue}
                  fabricDescription={this.state.fabricDescription}
                  fabricImage={this.state.fabricImage}
                />
              </div>
            )}
            <div className="w-full">
              {this.state.productIds.length ? (
                <ProductsGridStaticWithData
                  productIds={this.state.productIds}
                  material={this.state.material}
                  listName={this.state.isInactiveView ? 'MaterialSwatches' : ''}
                />
              ) : (
                <div className="flex flex-col w-full min-h-[50vh] items-center justify-center text-[.9rem] text-gray">
                  No products found for {this.state.material}.
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <Helmet>
              <title>Not Found</title>
            </Helmet>
            <div className="flex flex-col items-center justify-center my-20 bg-gray-100">
              <div className="text-center">
                <h1 className="text-6xl font-extrabold text-gray-800">
                  Not Found
                </h1>
                <p className="mt-4 text-xl text-gray-600">
                  {/* eslint-disable-next-line react/no-unescaped-entities */}
                  Oops! The page you're looking for doesn't exist.
                </p>
              </div>
            </div>
          </>
        )}
      </>
    );
  }
}

ProductsByMaterial.propTypes = {
  location: PropTypes.objectOf({
    search: PropTypes.string,
  }),
  match: PropTypes.objectOf({
    params: PropTypes.objectOf({
      swatch: PropTypes.string,
    }),
  }),
  staticContext: PropTypes.object,
};

ProductsGridStaticWithData.propTypes = {
  productIds: PropTypes.array,
  material: PropTypes.string,
  listName: PropTypes.string,
};

export default withRouter(ProductsByMaterial);
